/**
 * A project's commercial block (spec §4.1) and the spend it is measured by.
 *
 * These are the figures the Project Details sheet carries in its header:
 * what the client is billed, what we have agreed to pay out, and the
 * management fee. Margin is derived here and never stored — two copies of a
 * number are two numbers that can disagree.
 *
 * Pure. The caller fetches the campaign and its invoices and passes them in.
 */

/** Fields that make up the block. Anything outside this list is not ours. */
export const COMMERCIAL_FIELDS = [
  'project_code',
  'team_lead',
  'client_name',
  'client_billing_address',
  'client_gstin',
  'payment_terms',
  'client_cost',
  'internal_cost',
  'management_fees',
];

const MONEY_FIELDS = ['client_cost', 'internal_cost', 'management_fees'];
const GSTIN_RE = /^[0-9A-Z]{15}$/;

const squash = (value) => String(value ?? '').replace(/\s+/g, '').toUpperCase();

/** A finite number, or null when the value is blank or not a number. */
function money(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

const amount = (value) => money(value) ?? 0;

/** client cost − internal cost + management fees. Negative is a real answer. */
export function margin(commercials) {
  const c = commercials || {};
  return amount(c.client_cost) - amount(c.internal_cost) + amount(c.management_fees);
}

/**
 * Committed spend: every invoice that has not been rejected, counted from
 * submission rather than payment, because the point of the figure is to show
 * an overrun before it is paid. Creator and vendor lines count alike.
 */
export function spendOf(invoices) {
  const list = Array.isArray(invoices) ? invoices : [];
  return list
    .filter((invoice) => invoice && invoice.status !== 'rejected')
    .reduce((total, invoice) => total + amount(invoice.amount), 0);
}

/**
 * Spend against the agreed internal cost, or null when no internal cost has
 * been agreed — there is nothing to measure against, so the UI shows nothing.
 * Never blocks: an overrun is reported, not prevented (spec D6).
 */
export function spendStatus(spend, internalCost) {
  const budget = amount(internalCost);
  if (budget <= 0) return null;

  const spent = amount(spend);
  const over = spent > budget;
  return {
    spend: spent,
    budget,
    // Capped so the bar cannot overflow; the real overrun is reported beside it.
    percent: Math.min(100, Math.round((spent / budget) * 100)),
    over,
    overBy: over ? spent - budget : 0,
  };
}

/**
 * `{field: message}`; empty means valid. Every field is optional, so a project
 * can be opened before the commercials are agreed and completed later.
 */
export function validateCommercials(form) {
  const values = form || {};
  const errors = {};

  const gstin = squash(values.client_gstin);
  if (gstin && !GSTIN_RE.test(gstin)) {
    errors.client_gstin = 'Enter a valid 15-character GSTIN, e.g. 33AAIFF6389B1ZG.';
  }

  MONEY_FIELDS.forEach((field) => {
    const raw = values[field];
    if (raw === null || raw === undefined || String(raw).trim() === '') return;
    const parsed = money(raw);
    if (parsed === null) errors[field] = 'Enter an amount in numbers.';
    else if (parsed < 0) errors[field] = 'Enter an amount of zero or more.';
  });

  return errors;
}

/** The request body: money as numbers, text trimmed, blanks omitted. */
export function commercialsPayload(form) {
  const values = form || {};
  return COMMERCIAL_FIELDS.reduce((payload, field) => {
    const raw = values[field];
    if (raw === null || raw === undefined) return payload;

    if (MONEY_FIELDS.includes(field)) {
      const parsed = money(raw);
      return parsed === null ? payload : { ...payload, [field]: parsed };
    }

    const text = String(raw).trim();
    return text === '' ? payload : { ...payload, [field]: text };
  }, {});
}

/**
 * Whether the API returned any of the block at all.
 *
 * These fields do not exist on the campaign endpoint yet. Until they do, the
 * commercial sections hide rather than render empty — see the backend
 * requirements doc.
 */
export function hasCommercials(campaign) {
  const record = campaign || {};
  return COMMERCIAL_FIELDS.some(
    (field) => record[field] !== undefined && record[field] !== null && record[field] !== '',
  );
}
